import { MutableRefObject, useContext, useMemo, useState } from "react";
import { FEATURE_FLAG_NAME } from "common/constants";
import { AuthContext } from "../../Authorization/AuthContext";
import { FormDataType } from "../Form";
import ChatPanel from "./ChatPanel";
import ChatToggleButton from "./ChatToggleButton";

export interface CreateFormUpdateHandlerParams {
  setServerProvidedFormData: (data: FormDataType) => void;
  setDisabled: (disabled: boolean) => void;
  addSuccessToast: (message: string) => void;
  isMountedRef?: MutableRefObject<boolean>;
}

export const createFormUpdateHandler = ({
  setServerProvidedFormData,
  setDisabled,
  addSuccessToast,
  isMountedRef,
}: CreateFormUpdateHandlerParams) => {
  return (updates: any) => {
    if (!updates || typeof updates !== "object") {
      return;
    }
    if (isMountedRef && !isMountedRef.current) {
      return;
    }

    // The tool returns { formData: {...}, validationErrors?: [...], validationMessage?: string }
    // validationErrors are for the LLM's retry logic (not used by UI) - extract just formData
    const formData = updates.formData ?? updates;
    setServerProvidedFormData(formData);
    setDisabled(false);
    addSuccessToast("Form updated by AI assistant");
  };
};

export interface UseSubmissionsChatAgentProps {
  isPublic: boolean;
  submissionTypeVersionId?: string;
  formData?: FormDataType;
  onFormUpdate?: (updates: any) => void;
}

export const useSubmissionsChatAgent = ({
  isPublic,
  submissionTypeVersionId,
  formData,
  onFormUpdate,
}: UseSubmissionsChatAgentProps) => {
  const { isFeatureEnabled } = useContext(AuthContext);
  const isEnabled = isFeatureEnabled(FEATURE_FLAG_NAME.SUBMISSIONS_CHAT_AGENT);
  const [isOpen, setIsOpen] = useState(false);

  const ToggleButton = useMemo(() => {
    if (isPublic || !isEnabled) return null;
    return (
      <ChatToggleButton onClick={() => setIsOpen(!isOpen)} isOpen={isOpen} />
    );
  }, [isPublic, isEnabled, isOpen]);

  const Panel = useMemo(() => {
    if (!isEnabled || !submissionTypeVersionId || !formData || !onFormUpdate) {
      return null;
    }
    return (
      <ChatPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        submissionTypeVersionId={submissionTypeVersionId}
        formData={formData}
        onFormUpdate={onFormUpdate}
      />
    );
  }, [isEnabled, isOpen, submissionTypeVersionId, formData, onFormUpdate]);

  const isPanelOpen = isEnabled && isOpen && Boolean(submissionTypeVersionId);

  return {
    isEnabled,
    isOpen,
    setIsOpen,
    ChatToggleButton: ToggleButton,
    ChatPanel: Panel,
    isChatPanelOpen: isPanelOpen,
  } as const;
};

export default useSubmissionsChatAgent;
